import { AlertTitle, Box, Stack } from '@mui/material';
import { differenceInCalendarDays } from 'date-fns';
import { Button } from '../../../../primitives/button';
import { AlertBanner } from '../../../../primitives/alert/alert-banner';
import { Typography } from '../../../../primitives/typography/typography';
import type { CheckUpdateResult } from '../../../../utils/ota-updater/ota-updater';
import type { OtaLoginConfig } from '../login-page.types';

type OtaInstructionContentProps = {
  ota?: OtaLoginConfig;
  hasInactivityLogout: boolean;
  otaEnforcement: CheckUpdateResult | null;
  onAppStoreClick: () => void;
};

export const OtaInstructionContent = ({
  ota,
  hasInactivityLogout,
  otaEnforcement,
  onAppStoreClick,
}: OtaInstructionContentProps) => {
  if (!ota) {
    return null;
  }

  if (hasInactivityLogout) {
    return (
      <Stack spacing={2} sx={{ alignItems: 'center', width: '100%' }}>
        <AlertBanner severity="info" title="Session ended due to inactivity">
          <Typography.Subtitle>
            Please sign in again to continue accessing {ota.appName}.
          </Typography.Subtitle>
        </AlertBanner>
        <Typography.Body sx={{ textAlign: 'center' }}>
          Sign in to access {ota.appName}
        </Typography.Body>
      </Stack>
    );
  }

  if (!otaEnforcement) {
    return <Typography.Body>Sign in to access {ota.appName}</Typography.Body>;
  }

  const daysUntilMandatory = differenceInCalendarDays(
    new Date(otaEnforcement.enforcementDate),
    new Date(),
  );

  if (daysUntilMandatory > 0) {
    return (
      <Stack spacing={2} sx={{ alignItems: 'center' }}>
        <AlertBanner severity="info">
          <AlertTitle>New update available</AlertTitle>
          <Typography.Subtitle>
            Update the {ota.appName} app in the App Store to get access to the latest improvements
            and fixes
          </Typography.Subtitle>
          <Typography.Subtitle sx={{ fontWeight: 800 }}>
            This update will become mandatory in {daysUntilMandatory} days.
          </Typography.Subtitle>
          <Button color="info" sx={{ mt: 2 }} onClick={onAppStoreClick}>
            Go to App Store
          </Button>
        </AlertBanner>
        <Typography.Body sx={{ textAlign: 'center' }}>
          Sign in to access {ota.appName}
        </Typography.Body>
      </Stack>
    );
  }

  return (
    <Stack spacing={3} sx={{ alignItems: 'center' }}>
      <AlertBanner severity="error">
        <AlertTitle>Update required</AlertTitle>
        <Typography.Subtitle>
          We've made some mandatory changes to our systems. Please update the {ota.appName} app from
          the App Store to continue securely using the app.
        </Typography.Subtitle>
      </AlertBanner>
      <Box>
        <Button onClick={onAppStoreClick} color="primary" size="large">
          Update now
        </Button>
      </Box>
    </Stack>
  );
};
